import { motion } from 'motion/react';
import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { ANIMATION_CONFIG } from '../../../constants/animationConfig';
import { COLORS } from '../../../constants';
import { isSafariBrowser } from '../../../utils/isSafariBrowser';

export const MotionSecondaryHeaderBackground = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const isSafari = isSafariBrowser();
  if (isSafari) {
    return (
      <StaticBackground className={className}>
        {children}
      </StaticBackground>
    );
  }
  return (
    <MotionSecondaryHeaderBackgroundWrapper className={className}>
      {children}
    </MotionSecondaryHeaderBackgroundWrapper>
  );
};

const StaticBackground = styled.div`
  background: ${COLORS.HEADER_PORTFOLIO_BACKGROUND};
  overflow: hidden;
`;

const MotionSecondaryHeaderBackgroundWrapper = styled(motion.div)`
  @property --angle {
    syntax: '<angle>';
    initial-value: 270deg;
    inherits: false;
  }
  @property --stop1 {
    syntax: '<percentage>';
    initial-value: 16.1%;
    inherits: false;
  }
  @property --stop2 {
    syntax: '<percentage>';
    initial-value: 55.6%;
    inherits: false;
  }
  @property --stop3 {
    syntax: '<percentage>';
    initial-value: 100%;
    inherits: false;
  }
  @property --colorPrimary {
    syntax: '<color>';
    initial-value: #2f234c;
    inherits: false;
  }
  @property --colorSecondary {
    syntax: '<color>';
    initial-value: #2f234c;
    inherits: false;
  }

  overflow: hidden;
  --angle: 270deg;
  --stop1: 10.17%;
  --stop2: 56.05%;
  --stop3: 101.92%;
  background: linear-gradient(
    var(--angle),
    #2f234c var(--stop1),
    #2f234c var(--stop2),
    #2f234c var(--stop3)
  );
  animation: floating-gradient-secondary infinite;
  animation-duration: ${ANIMATION_CONFIG.keyFrames.secondaryHeader.duration}s;
  animation-delay: ${ANIMATION_CONFIG.keyFrames.secondaryHeader.delay}s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);

  @keyframes floating-gradient-secondary {
    0% {
      --angle: 270deg;

      --stop1: 26.1%;
      --stop2: 55.6%;
      --stop3: 80%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    12.5% {
      --angle: 270deg;

      --stop1: 5.1%;
      --stop2: 19.6%;
      --stop3: 40%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    25% {
      --angle: 270deg;

      --stop1: 5.1%;
      --stop2: 19.6%;
      --stop3: 40%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    37.5% {
      --angle: 270deg;
      
      --stop1: 5%;
      --stop2: 19.6%;
      --stop3: 40%;

      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    50% {
      --angle: 270deg;

      --stop1: 50%;
      --stop2: 75.6%;
      --stop3: 100%;

      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    62.5% {
      --angle: 270deg;

      --stop1: 50%;
      --stop2: 75.6%;
      --stop3: 100%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    75% {
      --angle: 270deg;

      --stop1: 50%;
      --stop2: 75.6%;
      --stop3: 100%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    87.5% {
      --angle: 270deg;

      --stop1: 26.1%;
      --stop2: 55.6%;
      --stop3: 80%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    100% {
      --angle: 270deg;

      --stop1: 26.1%;
      --stop2: 55.6%;
      --stop3: 80%;

      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
  }
`;
